import { useState } from "react";
import { Bell, MessageSquare, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import ThemeToggle from "./ThemeToggle";
import UserAvatar from "./UserAvatar";
import KudozDisplay from "./KudozDisplay";
import NavigationDrawer from "./NavigationDrawer";
import { useIsInsideConsumerLayout } from "@/contexts/ConsumerLayoutContext";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { logger } from "@/lib/logger";
const eatzyLogo = "/eatzy-disc-96.webp";

interface TopHeaderProps {
  user?: {
    id?: string;
    name: string;
    email?: string;
    avatar?: string;
    kudozBalance?: number;
  };
  unreadAlertCount?: number;
  unreadMessageCount?: number;
  onNotificationsClick: () => void;
  onMessagesClick: () => void;
  onProfileClick: () => void;
  onLogoClick?: () => void;
  startSlot?: React.ReactNode;
}

export default function TopHeader({
  user,
  unreadAlertCount = 0,
  unreadMessageCount = 0,
  onNotificationsClick,
  onMessagesClick,
  onProfileClick,
  onLogoClick,
  startSlot,
}: TopHeaderProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isInsideConsumerLayout = useIsInsideConsumerLayout();

  const handleNotifications = () => {
    logger.log("Notifications clicked");
    onNotificationsClick();
  };

  const handleMessages = () => {
    logger.log("Messages clicked");
    onMessagesClick();
  };

  const handleProfile = () => {
    logger.log("Profile clicked");
    onProfileClick();
  };

  return (
    <>
      <header
        className="sticky top-0 z-40 bg-card/80 backdrop-blur-sm border-b border-border"
        data-testid="top-header"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setDrawerOpen(true)}
              data-testid="button-menu"
              className="md:hidden"
            >
              <Menu className="w-5 h-5" />
            </Button>
            {startSlot}
            {!startSlot && isInsideConsumerLayout && (
              <div className="hidden md:flex">
                <SidebarTrigger />
              </div>
            )}
            <Link
              href="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="header-logo-link"
              onClick={onLogoClick}
            >
              <img src={eatzyLogo} alt="Eatzy" className="w-9 h-9" />
              <h1 className="font-bold text-lg leading-none">Eatzy</h1>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {user && (
              <Link href="/rewards">
                <div
                  className="hover-elevate active-elevate-2 rounded-full transition-all duration-200"
                  data-testid="kudoz-link"
                >
                  <KudozDisplay
                    amount={user.kudozBalance ?? 0}
                    size="sm"
                    variant="badge"
                  />
                </div>
              </Link>
            )}

            {user && (
              <div className="hidden lg:flex items-center gap-1 min-w-0 text-sm font-medium">
                <span className="text-foreground truncate max-w-[100px]">
                  {user.name}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleMessages}
                className="relative"
                data-testid="button-messages"
              >
                <MessageSquare className="w-4 h-4" />
                {unreadMessageCount > 0 && (
                  <span
                    className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full ring-2 ring-background animate-pulse"
                    data-testid="message-indicator"
                  />
                )}
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={handleNotifications}
                className="relative"
                data-testid="button-notifications"
              >
                <Bell className="w-4 h-4" />
                {unreadAlertCount > 0 && (
                  <span
                    className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full ring-2 ring-background animate-pulse"
                    data-testid="notification-indicator"
                  />
                )}
              </Button>

              <ThemeToggle />

              {user && (
                <Button
                  variant="ghost"
                  onClick={handleProfile}
                  className="p-1 h-auto"
                  data-testid="button-profile"
                >
                  <UserAvatar
                    src={user.avatar}
                    name={user.name}
                    size="sm"
                    showOnline
                  />
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <NavigationDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
}
